import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { ApiStatuses, NotificationPosition } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';
import { isObjInArray } from 'coreOld/utils';
import { ImportType } from 'corporate/enums';
import { ModalStep } from 'partsOld/enums';
import { useGetImportPreviewRows, useImportPartsActions } from 'partsOld/hooks';

import { DESCRIPTION, hiddenCostPillList, hiddenPricingPillList, PART_NUMBER } from './constants';
import { useDragAndDrop } from './dragAndDrop';
import {
  getImportHeaders,
  getPills,
  getPreviewSavePartsData,
  getQuantityHeaders,
  getRecognizedHeaders,
} from './helper';
import ImportModalView from './ImportModalView';

interface IProps {
  fileId: number;
  step: ModalStep;
  setStep: Dispatch<SetStateAction<ModalStep>>;
  onSubmit: (obj) => void;
  importType: ImportType;
  data;
}

const requestPayload = {
  page: 1,
  pageSize: 15,
  partImport: null,
};

const ImportModal = ({ fileId, step, setStep, data, onSubmit, importType }: IProps) => {
  const headers = getImportHeaders(importType);

  const [filters, setFilters] = useState(requestPayload);
  const [recognizedHeaders, setRecognizedHeaders] = useState(null);
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [pricingCheckbox, setPricingCheckbox] = useState(false);
  const [quantityCheckbox, setQuantityCheckbox] = useState(false);
  const [addNewHeader, setAddNewHeader] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [initialPills, setInitialPills] = useState(headers);
  const [isComponentRendered, setIsComponentRendered] = useState(false);

  const { pills, addPills, removePills, clearPills } = useDragAndDrop();
  const { showNotification, clearNotification } = useNotification();

  const { importCreate, isImportCreateLoading } = useImportPartsActions();

  const { records, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useGetImportPreviewRows(filters, isUploadSuccessful, () => setInitialized(true));

  useEffect(() => {
    if (pills) {
      const items = getPills(pricingCheckbox, quantityCheckbox, headers);

      setInitialPills(items.filter((el) => !isObjInArray(el, 'id', pills)));
    }
  }, [pills, pricingCheckbox, quantityCheckbox]);

  useEffect(() => {
    if (recognizedHeaders && initialized) {
      const headers = getRecognizedHeaders(records, recognizedHeaders);

      addPills(headers);

      setInitialized(false);
    }
  }, [initialized]);

  useEffect(() => {
    if (fileId && isComponentRendered) {
      onInit();
    }
  }, [isComponentRendered, fileId]);

  useEffect(() => {
    setIsComponentRendered(true);
  }, []);

  const onCheckboxChange = (isPricing: boolean) => {
    if (isPricing) {
      if (pricingCheckbox) {
        setPricingCheckbox(false);

        setInitialPills((prev) => prev.filter((item) => headers.some((el) => el.id !== item.id)));

        removePills(hiddenPricingPillList);

        return;
      }

      setPricingCheckbox(true);
      setInitialPills((prev) => prev.concat(hiddenPricingPillList));

      return;
    }

    if (quantityCheckbox) {
      setQuantityCheckbox(false);

      removePills(hiddenCostPillList);

      return;
    }

    setQuantityCheckbox(true);

    addPills(getQuantityHeaders(hiddenCostPillList, records?.[0]));
  };

  const onInit = async () => {
    const createdImport = await importCreate({ file: fileId });

    onSubmit({ partImport: createdImport.importId });
    setFilters({ ...filters, partImport: createdImport.importId });

    const reqHeaders = headers.reduce((obj, field) => {
      if (createdImport.recognizedHeaders[field.id]) {
        return {
          ...obj,
          [field.id]: createdImport.recognizedHeaders[field.id],
        };
      } else {
        return {
          ...obj,
        };
      }
    }, {});

    setRecognizedHeaders(reqHeaders);

    setIsUploadSuccessful(true);
  };

  const clearHeaders = () => {
    clearPills();
    setInitialPills(headers);
  };

  const onConfirmMapping = async () => {
    const columnAliases = pills.reduce((obj, field) => {
      return { ...obj, [field.id]: !addNewHeader ? field.columnHeader : '' };
    }, {});

    onSubmit({
      ...data,
      columnAliases,
      previewSavePartsData: getPreviewSavePartsData(data, pills, addNewHeader),
    });

    const isHeaderOrDescriptionHeaderPresent = pills.find(
      (el) => el.id === PART_NUMBER || el.id === DESCRIPTION
    );

    if (!isHeaderOrDescriptionHeaderPresent) {
      showNotification({
        type: ApiStatuses.ERROR,
        message: 'Either the number or the description header must be present.',
        position: NotificationPosition.TOP,
      });

      return;
    }

    clearNotification();
    setStep(ModalStep.PREVIEW_ROWS);
  };

  return (
    <ImportModalView
      {...{
        records,
        isUploadSuccessful,
        initialPills,
        clearHeaders,
        isLoading: isLoading || isImportCreateLoading,
        onCheckboxChange,
        pills,
        pricingCheckbox,
        quantityCheckbox,
        addNewHeader,
        setAddNewHeader,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        onConfirmMapping,
        data,
        step,
        setStep,
      }}
    />
  );
};

export default ImportModal;
